import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useAgent } from './AgentProvider';

// Global chat context
const GlobalChatContext = createContext();

// Chat window states
const CHAT_STATES = {
  CLOSED: 'closed',
  MINIMIZED: 'minimized',
  OPEN: 'open'
};

/**
 * GlobalAIChatProvider - Provides global chat access throughout the app
 *
 * Features:
 * - Global chat window state management
 * - Keyboard shortcuts (Cmd+K, Esc)
 * - Conversation persistence
 * - Position and size management
 * - Multiple conversation support
 */
const GlobalAIChatProvider = ({ children, defaultAgent = 'succession_dashboard_agent' }) => {
  // Chat window state
  const [chatState, setChatState] = useState(CHAT_STATES.CLOSED);
  const [chatPosition, setChatPosition] = useState({ x: null, y: null });
  const [chatSize, setChatSize] = useState({ width: 500, height: 600 });
  const [isMaximized, setIsMaximized] = useState(false);

  // Conversation management
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // Get agent context
  const {
    conversationId,
    activeAgentType,
    startConversation,
    switchAgent
  } = useAgent();

  // Load persisted state on mount
  useEffect(() => {
    const loadPersistedState = () => {
      try {
        const persisted = localStorage.getItem('globalChat_state');
        if (persisted) {
          const state = JSON.parse(persisted);
          if (state.conversations) {
            setConversations(state.conversations);
          }
          if (state.activeConversationId) {
            setActiveConversationId(state.activeConversationId);
          }
          if (state.chatPosition) {
            setChatPosition(state.chatPosition);
          }
          if (state.chatSize) {
            setChatSize(state.chatSize);
          }
        }
      } catch (error) {
        console.error('Failed to load persisted chat state:', error);
      }
    };

    loadPersistedState();
  }, []);

  // Persist state on changes
  useEffect(() => {
    try {
      const state = {
        conversations: conversations.slice(-10), // Keep last 10
        activeConversationId,
        chatPosition,
        chatSize
      };
      localStorage.setItem('globalChat_state', JSON.stringify(state));
    } catch (error) {
      console.error('Failed to persist chat state:', error);
    }
  }, [conversations, activeConversationId, chatPosition, chatSize]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd+K or Ctrl+K to toggle chat
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        toggleChat();
      }

      // Esc to minimize chat
      if (e.key === 'Escape' && chatState === CHAT_STATES.OPEN) {
        minimizeChat();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [chatState]);

  // Open chat
  const openChat = useCallback(async (options = {}) => {
    const {
      agentType = defaultAgent,
      message = null,
      context = {}
    } = options;

    // Start conversation if needed
    if (!conversationId) {
      await startConversation(agentType, context);
    }

    // Set active conversation
    if (conversationId && !activeConversationId) {
      setActiveConversationId(conversationId);
    }

    // Add to conversations list if new
    if (conversationId && !conversations.find(c => c.id === conversationId)) {
      setConversations(prev => [
        ...prev,
        {
          id: conversationId,
          agentType: agentType,
          startedAt: new Date().toISOString(),
          lastActivity: new Date().toISOString(),
          unread: 0
        }
      ]);
    }

    setChatState(CHAT_STATES.OPEN);
    setUnreadCount(0);

    return { conversationId, message };
  }, [conversationId, activeConversationId, conversations, defaultAgent, startConversation]);

  // Close chat
  const closeChat = useCallback(() => {
    setChatState(CHAT_STATES.CLOSED);
  }, []);

  // Minimize chat
  const minimizeChat = useCallback(() => {
    setChatState(CHAT_STATES.MINIMIZED);
  }, []);

  // Maximize chat
  const maximizeChat = useCallback(() => {
    setIsMaximized(true);
    setChatState(CHAT_STATES.OPEN);
  }, []);

  // Restore chat size
  const restoreChat = useCallback(() => {
    setIsMaximized(false);
    setChatState(CHAT_STATES.OPEN);
  }, []);

  // Toggle chat
  const toggleChat = useCallback(() => {
    if (chatState === CHAT_STATES.OPEN) {
      minimizeChat();
    } else {
      openChat();
    }
  }, [chatState, openChat, minimizeChat]);

  // Update chat position
  const updatePosition = useCallback((x, y) => {
    setChatPosition({ x, y });
  }, []);

  // Update chat size
  const updateSize = useCallback((width, height) => {
    setChatSize({ width, height });
  }, []);

  // Switch conversation
  const switchConversation = useCallback((conversationId) => {
    setActiveConversationId(conversationId);
    const conversation = conversations.find(c => c.id === conversationId);
    if (conversation) {
      // Mark as read
      setConversations(prev =>
        prev.map(c =>
          c.id === conversationId ? { ...c, unread: 0 } : c
        )
      );
      setUnreadCount(prev => Math.max(0, prev - conversation.unread));
    }
  }, [conversations]);

  // Create new conversation
  const createConversation = useCallback(async (agentType = defaultAgent) => {
    const result = await startConversation(agentType, {});
    if (result?.conversationId) {
      setConversations(prev => [
        ...prev,
        {
          id: result.conversationId,
          agentType: agentType,
          startedAt: new Date().toISOString(),
          lastActivity: new Date().toISOString(),
          unread: 0
        }
      ]);
      setActiveConversationId(result.conversationId);
      setChatState(CHAT_STATES.OPEN);
    }
    return result;
  }, [defaultAgent, startConversation]);

  // Delete conversation
  const deleteConversation = useCallback((conversationId) => {
    setConversations(prev => prev.filter(c => c.id !== conversationId));
    if (activeConversationId === conversationId) {
      const remaining = conversations.filter(c => c.id !== conversationId);
      setActiveConversationId(remaining.length > 0 ? remaining[0].id : null);
    }
  }, [activeConversationId, conversations]);

  // Mark conversation as active
  const updateConversationActivity = useCallback((conversationId) => {
    setConversations(prev =>
      prev.map(c =>
        c.id === conversationId
          ? { ...c, lastActivity: new Date().toISOString() }
          : c
      )
    );
  }, []);

  // Increment unread for conversation
  const incrementUnread = useCallback((conversationId) => {
    if (chatState !== CHAT_STATES.OPEN || activeConversationId !== conversationId) {
      setConversations(prev =>
        prev.map(c =>
          c.id === conversationId
            ? { ...c, unread: (c.unread || 0) + 1 }
            : c
        )
      );
      setUnreadCount(prev => prev + 1);
    }
  }, [chatState, activeConversationId]);

  // Context value
  const contextValue = {
    // State
    chatState,
    chatPosition,
    chatSize,
    isMaximized,
    conversations,
    activeConversationId,
    unreadCount,
    isOpen: chatState === CHAT_STATES.OPEN,
    isMinimized: chatState === CHAT_STATES.MINIMIZED,
    isClosed: chatState === CHAT_STATES.CLOSED,

    // Actions
    openChat,
    closeChat,
    minimizeChat,
    maximizeChat,
    restoreChat,
    toggleChat,
    updatePosition,
    updateSize,

    // Conversation management
    conversations,
    activeConversationId,
    switchConversation,
    createConversation,
    deleteConversation,
    updateConversationActivity,
    incrementUnread
  };

  return (
    <GlobalChatContext.Provider value={contextValue}>
      {children}
    </GlobalChatContext.Provider>
  );
};

GlobalAIChatProvider.propTypes = {
  children: PropTypes.node.isRequired,
  defaultAgent: PropTypes.string
};

// Hook to use global chat
export const useGlobalChat = () => {
  const context = useContext(GlobalChatContext);
  if (!context) {
    throw new Error('useGlobalChat must be used within GlobalAIChatProvider');
  }
  return context;
};

export default GlobalAIChatProvider;
